import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { Users, DollarSign, Activity, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

interface BetaUser {
  id: string;
  user_id: string;
  beta_tier: 'free' | 'premium' | 'unlimited';
  ai_credits_remaining: number;
  daily_limit: number;
  monthly_budget_cents: number;
  monthly_spend_cents: number;
  last_reset_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  profile?: {
    email: string;
    full_name: string;
  };
  daily_usage?: {
    operations_count: number;
    total_cost_cents: number;
  };
}

const BetaUserManagement = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [users, setUsers] = useState<BetaUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');

  const fetchBetaUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch beta user configs with profile data
      const { data: betaUsers, error: betaError } = await supabase
        .from('beta_user_config')
        .select(`
          *,
          profiles!beta_user_config_user_id_fkey (
            email,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (betaError) throw betaError;

      // Fetch today's usage for each user
      const today = new Date().toISOString().split('T')[0];
      const userIds = betaUsers.map(u => u.user_id);
      
      const { data: dailyUsage, error: usageError } = await supabase
        .from('daily_ai_usage')
        .select('user_id, operations_count, total_cost_cents')
        .eq('usage_date', today)
        .in('user_id', userIds);

      if (usageError) {
        console.warn('Error fetching daily usage:', usageError);
      }

      // Combine data
      const usersWithUsage = betaUsers.map(user => ({
        ...user,
        beta_tier: user.beta_tier as 'free' | 'premium' | 'unlimited',
        profile: (user as any).profiles?.[0] || null,
        daily_usage: dailyUsage?.find(usage => usage.user_id === user.user_id) || null
      }));

      setUsers(usersWithUsage as BetaUser[]);
    } catch (error: any) {
      console.error('Error fetching beta users:', error);
      toast.error('Failed to load beta users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserTier = async (userId: string, newTier: 'free' | 'premium' | 'unlimited') => {
    try {
      const updates = {
        beta_tier: newTier,
        ai_credits_remaining: newTier === 'free' ? 100 : newTier === 'premium' ? 500 : 999999,
        daily_limit: newTier === 'free' ? 10 : newTier === 'premium' ? 50 : 999999,
        monthly_budget_cents: newTier === 'free' ? 500 : newTier === 'premium' ? 2000 : 999999
      };

      const { error } = await supabase
        .from('beta_user_config')
        .update(updates)
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('User tier updated successfully');
      fetchBetaUsers();
    } catch (error: any) {
      console.error('Error updating user tier:', error);
      toast.error('Failed to update user tier');
    }
  };

  const resetUserUsage = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('beta_user_config')
        .update({
          ai_credits_remaining: users.find(u => u.user_id === userId)?.beta_tier === 'free' ? 100 : 500,
          monthly_spend_cents: 0,
          last_reset_date: new Date().toISOString().split('T')[0]
        })
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('User usage reset successfully');
      fetchBetaUsers();
    } catch (error: any) {
      console.error('Error resetting user usage:', error);
      toast.error('Failed to reset user usage');
    }
  };

  const getBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'free': return 'secondary';
      case 'premium': return 'default';
      case 'unlimited': return 'destructive';
      default: return 'secondary';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.profile?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profile?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = filterTier === 'all' || user.beta_tier === filterTier;
    
    return matchesSearch && matchesTier;
  });

  const totalUsers = users.length;
  const totalActiveUsers = users.filter(u => u.is_active).length;
  const totalSpendCents = users.reduce((sum, u) => sum + u.monthly_spend_cents, 0);
  const totalOperationsToday = users.reduce((sum, u) => sum + (u.daily_usage?.operations_count || 0), 0);

  useEffect(() => {
    if (user && isAdmin) {
      fetchBetaUsers();
    }
  }, [user, isAdmin]);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>You need admin privileges to access this page.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              {totalActiveUsers} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(totalSpendCents / 100).toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              This month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Operations Today</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOperationsToday}</div>
            <p className="text-xs text-muted-foreground">
              AI operations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Cost/Op</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${totalOperationsToday > 0 ? ((totalSpendCents / 100) / totalOperationsToday).toFixed(3) : '0.000'}
            </div>
            <p className="text-xs text-muted-foreground">
              Per operation
            </p>
          </CardContent>
        </Card>
      </div>

      {/* User Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Beta User Management</CardTitle>
              <CardDescription>Manage beta user tiers and usage limits</CardDescription>
            </div>
            <Button onClick={fetchBetaUsers} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="unlimited">Unlimited</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading users...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Today's Usage</TableHead>
                  <TableHead>Monthly Spend</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {user.profile?.full_name || 'Unknown User'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.profile?.email || user.user_id}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBadgeVariant(user.beta_tier)}>
                        {user.beta_tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.beta_tier === 'unlimited' ? '∞' : user.ai_credits_remaining}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div>{user.daily_usage?.operations_count || 0} ops</div>
                        <div className="text-sm text-muted-foreground">
                          ${((user.daily_usage?.total_cost_cents || 0) / 100).toFixed(3)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div>${(user.monthly_spend_cents / 100).toFixed(2)}</div>
                        <div className="text-sm text-muted-foreground">
                          / ${(user.monthly_budget_cents / 100).toFixed(2)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Select
                          value={user.beta_tier}
                          onValueChange={(value) => updateUserTier(user.user_id, value as any)}
                        >
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                            <SelectItem value="unlimited">Unlimited</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={() => resetUserUsage(user.user_id)}
                          variant="outline"
                          size="sm"
                        >
                          Reset
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BetaUserManagement;